import React, { useEffect, useState } from 'react';
import { getMyProjects, GetUsers } from '../../Api/ApiUrlConst';
import { toast } from 'react-toastify';
import { Assignment, CheckCircle, Folder, HourglassEmpty, Person } from '@mui/icons-material';
import {
  Button,
  Col,
  Row,
  Card,
  CardBody,
  Container,
} from 'reactstrap';
import { getTeamLeadProjects } from '../../Api/ApiUrlConst'; // Adjust path as per your structure
import { Tooltip } from '@mui/material';


interface SubTask {
  id: number;
  title: string;
  dueDate: string;
  status: string;
  workHours: number;
  completionDate: string;
  remarks: string;
}

interface Task {
  id: number;
  title: string;
  dueDate: string;
  status: string;
  priority: string;
  percentCompleted: number;
  remarks: string;
  completionDate: string;
  subTasks: SubTask[];
}

interface Project {
  id: number;
  projectId: string;
  projectName: string;
  description: string;
  teamLeadNames: string[]; // updated to array of strings
  createdByName: string;
  startDate?: string | null;
  endDate?: string | null;
  status: string;
  remarks: string;
  tasks?: Task[];
}

interface TeamMember {
  id: string;
  name: string;
  role: string;
  department?: string;
}

const PMDashComponents: React.FC = () => {
  const [refresh, setRefresh] = useState(false);
  const [allProjects, setAllProjects] = useState<Project[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTeamLeadId, setSelectedTeamLeadId] = useState<string | null>(null);
  const [selectedTeamLeadName, setSelectedTeamLeadName] = useState<string>('');
  const [teamLeadProjects, setTeamLeadProjects] = useState<any[]>([]);



  const [projectStats, setProjectStats] = useState({
    total: 0,
    active: 0,
    completed: 0,
    pending: 0
  });

  const normalizeStatus = (status?: string) =>
    status?.replace(/\s/g, "").toLowerCase() || "";


  const COMPLETED_STATUSES = [
    "completed",
    "released"
  ];

  const ACTIVE_STATUSES = [
    "modellingtodo",
    "modellinginprogress",
    "draftingtodo",
    "draftinginprogress",
    "qctodo",
    "qcinprogress",
    "clientreviewtodo",
    "clientreviewinprogress",
    "finalcorrectiontodo",
    "finalcorrectioninprogress",
    "revisiontodo",
    "revisioninprogress",
    "modellingcompleted",
    "draftingcompleted",
    "qccompleted",
    "clientreviewcompleted",
    "finalcorrectioncompleted",
    "revisioncompleted",
  ];

  const PENDING_STATUSES = [
    "onhold",
    "issues",
    "cancelled",
    "yettostart",
    "waitingforinput",
  ];


  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const res = await getMyProjects();
        const data = Array.isArray(res?.data) ? res.data : res?.data?.$values || [];

        const projects: Project[] = data.map((p: any) => ({
          ...p,
          projectId: p.project_ID,
          teamLeadNames: p.teamLeadNames ?? [], // use teamLeadNames from backend
          createdByName: p.createdByName || '—',
        }));

        const total = projects.length;
        const completed = projects.filter(p =>
          COMPLETED_STATUSES.includes(normalizeStatus(p.status))
        ).length;

        const active = projects.filter(p =>
          ACTIVE_STATUSES.includes(normalizeStatus(p.status))
        ).length;

        const pending = projects.filter(p =>
          PENDING_STATUSES.includes(normalizeStatus(p.status))
        ).length;

        setAllProjects(projects);
        setProjectStats({ total, active, completed, pending });

      } catch (err) {
        toast.error('Failed to fetch project stats');
      }
    };

    fetchProjects();
  }, [refresh]);

  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        setLoading(true);
        const res = await GetUsers();
        const data = Array.isArray(res?.data) ? res.data : res?.data?.$values || [];
        setTeamMembers(data);
        setError(null);
      } catch (err: any) {
        setError(err.message || 'Failed to fetch team members');
      } finally {
        setLoading(false);
      }
    };

    fetchTeamMembers();
  }, []);

  const filteredProjects =
    selectedStatus === "all"
      ? allProjects
      : allProjects.filter(p => {
        const s = normalizeStatus(p.status);

        if (selectedStatus === "active") return ACTIVE_STATUSES.includes(s);
        if (selectedStatus === "completed") return COMPLETED_STATUSES.includes(s);
        if (selectedStatus === "YetToStart") return PENDING_STATUSES.includes(s);

        return true;
      });

  const statusColorMap: Record<string, string> = {
    'Yet to Start': 'status-yet-to-start',
    YetToStart: 'status-yet-to-start',

    'On Hold': 'status-on-hold',
    OnHold: 'status-on-hold',

    'Waiting for Input': 'status-waiting-input',
    WaitingForInput: 'status-waiting-input',

    'Modelling in Progress': 'status-modelling-progress',
    ModellingInProgress: 'status-modelling-progress',

    'Modelling ToDo': 'status-modelling-Todo',
    ModellingTodo: 'status-modelling-Todo',

    'Modelling Completed': 'status-modelling-completed',
    ModellingCompleted: 'status-modelling-completed',

    'Drafting in Progress': 'status-drafting-progress',
    DraftingInProgress: 'status-drafting-progress',

    'Drafting ToDo': 'status-drafting-Todo',
    DraftingTodo: 'status-drafting-Todo',

    'Drafting Completed': 'status-drafting-completed',
    DraftingCompleted: 'status-drafting-completed',

    'QC ToDo': 'status-qc-Todo',
    QCTodo: 'status-qc-Todo',

    'QC in Progress': 'status-qc-progress',
    QCInProgress: 'status-qc-progress',

    'QC Completed': 'status-qc-completed',
    QCCompleted: 'status-qc-completed',

    'Client Review in Progress': 'status-client-review-progress',
    ClientReviewInProgress: 'status-client-review-progress',

    'Client Review ToDo': 'status-client-review-Todo',
    ClientReviewTodo: 'status-client-review-Todo',



    'Client Review Completed': 'status-client-review-completed',
    ClientReviewCompleted: 'status-client-review-completed',

    'Revision in Progress': 'status-Revision-progress',
    RevisionInProgress: 'status-Revision-progress',

    'Revision ToDo': 'status-Revision-Todo',
    RevisionTodo: 'status-Revision-Todo',

    'Revision Completed': 'status-Revision-completed',
    RevisionCompleted: 'status-Revision-completed',


    'Final Correction in Progress': 'status-final-correction-progress',
    FinalCorrectionInProgress: 'status-final-correction-progress',

    'Final Correction ToDo': 'status-final-correction-Todo',
    FinalCorrectionTodo: 'status-final-correction-Todo',

    'Final Correction Completed': 'status-final-correction-completed',
    FinalCorrectionCompleted: 'status-final-correction-completed',

    'Not In Scope': 'status-Not-In-Scope',
    NotInScope: 'status-Not-In-Scope',

    Released: 'status-released',
    'Completed & Closed': 'status-completed',
    Completed: 'status-completed',

    Cancelled: 'status-cancelled',
    Issues: 'status-issues',

 CompletedAndReleased: "status-completedandreleased",


  NotApplicable1: "status-Drafting-Released",
  };
  const handleTeamLeadClick = async (teamLeadId: string, teamLeadName: string) => {
    setSelectedTeamLeadId(teamLeadId);
    setSelectedTeamLeadName(teamLeadName);

    try {
      const response = await getTeamLeadProjects(); // This returns only current user's projects (TeamLead)
      const allProjects = response.data;

      // Filter projects by clicked teamLeadId
      const filteredProjects = allProjects.filter(
        (project: any) => project.teamLeadId === teamLeadId && project.status !== 'Completed'
      );

      setTeamLeadProjects(filteredProjects);
    } catch (error) {
      console.error("Error fetching team lead projects", error);
    }
  };


  const formatDate = (dateStr?: string | null): string => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };




 const formatStatusLabel = (status: string) => {
  const overrides: Record<string, string> = {
    Completed: "Ready for Release",
    YetToStart: "Yet to Start",
    NotApplicable: "Not In Scope",
  };

  if (overrides[status]) {
    return overrides[status];
  }

  return status
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/([A-Z])([A-Z][a-z])/g, "$1 $2")
    .replace(/And/g, "&")
    .trim();
};





  return (
    <Container fluid className="mb-4">
      <Row className="mt-4">
        {[
          { key: "all", label: "Total Projects", value: projectStats.total, icon: <Folder />, bg: "#aecff0ff", iconColor: "#1976d2" },
          { key: "active", label: "Active Projects", value: projectStats.active, icon: <Assignment />, bg: "#bfeec1ff", iconColor: "#388e3c" },
          { key: "completed", label: "Completed Projects", value: projectStats.completed, icon: <CheckCircle />, bg: "#bbebbdff", iconColor: "#4caf50" },
          { key: "YetToStart", label: "Pending Projects", value: projectStats.pending, icon: <HourglassEmpty />, bg: "#fadcafff", iconColor: "#ff9900" },
        ].map(stat => (
          <Col md={3} key={stat.key}>
            <Card
              onClick={() => setSelectedStatus(stat.key)}
              className={`d-flex flex-row align-items-center p-3 ${selectedStatus === stat.key ? 'border border-dark' : ''}`}
              style={{ cursor: 'pointer' }}
            >
              <div style={{ borderRadius: "50%", padding: "10px", background: stat.bg }}>
                {React.cloneElement(stat.icon, { style: { color: stat.iconColor, fontSize: 30 } })}
              </div>
              <div className="ms-3">
                <h6 className="mb-1">{stat.label}</h6>
                <h4 className="fw-bold">{stat.value}</h4>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {selectedStatus && (
        <Card className="mt-4 shadow-sm border-0">
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4 className="fw-semibold text-primary mb-0 text-capitalize">
                {selectedStatus === "all" ? "All Projects" : `${selectedStatus} Projects`}
              </h4>
              <Button size="sm" color="secondary" onClick={() => setSelectedStatus(null)}>
                Hide
              </Button>
            </div>

            {filteredProjects.length === 0 ? (
              <p className="text-muted">No projects found.</p>
            ) : (
              <div className="table-responsive">
                <table className="table table-striped table-bordered table-hover align-middle">
                  <thead className="table-dark text-center">
                    <tr>
                      <th>SNo</th>
                      <th>Project Name</th>
                      <th>Team Leads</th>
                      <th>Start Date</th>
                      <th>End Date</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProjects.map((project, index) => {
                      return (
                        <tr key={project.id} className="text-center align-middle">
                          <td>{index + 1}</td>
                          <td className="fw-medium text-start">{project.projectName}</td>
                          <td className="text-start">
                            {project.teamLeadNames && project.teamLeadNames.length > 0 ? (
                              project.teamLeadNames.map((name, idx) => (
                                <span className="badge bg-secondary me-1" key={idx}>
                                  {name}
                                </span>
                              ))
                            ) : (
                              <span>—</span>
                            )}
                          </td>
                          <td>{formatDate(project.startDate)}</td>
                          <td>{formatDate(project.endDate)}</td>
                          <td>
                            <td className="text-center align-middle">
                              <span
                                className={`badge px-3 py-2 rounded-pill text-capitalize ${statusColorMap[project.status] || 'bg-secondary'}`}
                              >
                                {formatStatusLabel(project.status)}
                              </span>
                            </td>


                          </td>

                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>
      )}


      <Row className="mt-5">
        {/* Team Leads */}
        <Col sm={12} lg={6}>
          <Card className="shadow-sm rounded-4 p-4 mb-4">
            <h5 className="mb-4 fw-bold text-primary">👥 Team Leads</h5>
            {loading ? (
              <p className="text-muted">Loading...</p>
            ) : error ? (
              <p className="text-danger">{error}</p>
            ) : teamMembers.filter((tm) => tm.role === 'TeamLeader').length === 0 ? (
              <p className="text-muted">No team leads found.</p>
            ) : (
              <Row className="g-3">
                {teamMembers
                  .filter((tm) => tm.role === 'TeamLeader')
                  .map((tm) => (
                    <Col sm="6" md="4" key={tm.id}>
                      <Card
                        className="border-0 shadow-sm rounded-3"
                        onClick={() => handleTeamLeadClick(tm.id, tm.name)}
                        style={{ cursor: 'pointer' }}
                      ><CardBody>
                          <Tooltip title={tm.department} arrow>
                            <div className="d-flex align-items-center gap-2">
                              <Person className="text-primary" />
                              <span className="fw-semibold">{tm.name}</span>
                            </div>
                          </Tooltip>
                        </CardBody>

                      </Card>
                    </Col>
                  ))}
              </Row>
            )}
          </Card>
        </Col>

        {/* Team Members */}
        <Col sm={12} lg={6}>
          <Card className="shadow-sm rounded-4 p-4 mb-4">
            <h5 className="mb-4 fw-bold text-primary">👤 Team Members</h5>

            {loading ? (
              <p className="text-muted">Loading...</p>
            ) : error ? (
              <p className="text-danger">{error}</p>
            ) : teamMembers.filter((tm) => tm.role === 'TeamMember').length === 0 ? (
              <p className="text-muted">No team members found.</p>
            ) : (
              <Row className="g-3">
                {teamMembers
                  .filter((tm) => tm.role === 'TeamMember')
                  .map((tm) => (
                    <Col sm="6" md="4" key={tm.id}>
                      <Card
                        className="border-0 shadow-sm rounded-3 hover-card"
                        style={{ cursor: "pointer" }}
                      >
                        <CardBody className="d-flex flex-column align-items-start gap-2">
                          <div className="d-flex align-items-center gap-2">
                            <Tooltip title={tm.department} arrow>
                              <span className="d-flex align-items-center">
                                <Person className="text-primary" />
                                <span className="fw-semibold">{tm.name}</span>
                              </span>
                            </Tooltip>
                          </div>
                        </CardBody>
                      </Card>
                    </Col>
                  ))}
              </Row>
            )}
          </Card>
        </Col>

      </Row>

    </Container>
  );
};

export default PMDashComponents;
